import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { HotToastService } from '@ngxpert/hot-toast';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { ActiveYearService } from '../../../services/active-year.service';
import { AppSettingsService } from '../../../services/app-settings.service';
import { Helpers } from '../../../utilities/helpers';

@Component({
  selector: 'app-active-year-settings',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSlideToggleModule,
    NgxSpinnerModule
  ],
  templateUrl: './active-year-settings.component.html',
  styleUrl: './active-year-settings.component.scss'
})
export class ActiveYearSettingsComponent implements OnInit {
  private fb = inject(FormBuilder);
  private activeYearService = inject(ActiveYearService);
  private appSettingsService = inject(AppSettingsService);
  private toast = inject(HotToastService);
  private spinner = inject(NgxSpinnerService);

  form: FormGroup = this.fb.group({
    activeYear: [
      null,
      [Validators.required, Validators.min(1900), Validators.max(2100), this.notBackwardsValidator()]
    ]
  });

  cleanupForm: FormGroup = this.fb.group({
    enabled: [true],
    retentionDays: [90, [Validators.required, Validators.min(1), Validators.max(3650)]],
    hourUtc: [4, [Validators.required, Validators.min(0), Validators.max(23)]]
  });

  rebuildForm: FormGroup = this.fb.group({
    enabled: [true],
    hourUtc: [3, [Validators.required, Validators.min(0), Validators.max(23)]],
    batchSize: [1000, [Validators.required, Validators.min(1), Validators.max(5000)]]
  });

  initialYear: number | null = null;

  private notBackwardsValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (this.initialYear === null || control.value === null || control.value === '') {
        return null;
      }
      return control.value < this.initialYear ? { backwards: { min: this.initialYear } } : null;
    };
  }

  ngOnInit(): void {
    this.initialYear = this.activeYearService.current();
    this.form.patchValue({ activeYear: this.initialYear });
    this.form.get('activeYear')?.updateValueAndValidity();

    this.loadCleanupSettings();
    this.loadRebuildSettings();
  }

  // --- Active year ------------------------------------------------------

  get isUnchanged(): boolean {
    return this.form.value.activeYear === this.initialYear;
  }

  onSubmit(): void {
    if (this.form.invalid || this.isUnchanged) {
      return;
    }

    const newYear: number = this.form.value.activeYear;
    this.spinner.show();
    this.activeYearService.update(newYear).subscribe({
      next: (savedYear) => {
        this.initialYear = savedYear;
        this.spinner.hide();
        this.toast.success(`Active year set to ${savedYear}`);
      },
      error: (error) => {
        console.error('Error saving active year:', error);
        this.spinner.hide();
        this.toast.error(`<strong>Failed to save active year:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  // --- Login-attempt cleanup -------------------------------------------

  private loadCleanupSettings(): void {
    this.appSettingsService.getLoginAttemptCleanupSettings().subscribe({
      next: (settings) => {
        this.cleanupForm.patchValue(settings);
        this.cleanupForm.markAsPristine();
      },
      error: (error) => console.error('Error loading login-attempt cleanup settings:', error)
    });
  }

  saveCleanupSettings(): void {
    if (this.cleanupForm.invalid || this.cleanupForm.pristine) {
      return;
    }

    this.spinner.show();
    this.appSettingsService.updateLoginAttemptCleanupSettings(this.cleanupForm.getRawValue()).subscribe({
      next: (settings) => {
        this.cleanupForm.patchValue(settings);
        this.cleanupForm.markAsPristine();
        this.spinner.hide();
        this.toast.success('Login attempt cleanup settings saved');
      },
      error: (error) => {
        console.error('Error saving login-attempt cleanup settings:', error);
        this.spinner.hide();
        this.toast.error(`<strong>Failed to save cleanup settings:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  // --- Customer-report rebuild -----------------------------------------

  private loadRebuildSettings(): void {
    this.appSettingsService.getCustomerReportRebuildSettings().subscribe({
      next: (settings) => {
        this.rebuildForm.patchValue(settings);
        this.rebuildForm.markAsPristine();
      },
      error: (error) => console.error('Error loading customer-report rebuild settings:', error)
    });
  }

  saveRebuildSettings(): void {
    if (this.rebuildForm.invalid || this.rebuildForm.pristine) {
      return;
    }

    this.spinner.show();
    this.appSettingsService.updateCustomerReportRebuildSettings(this.rebuildForm.getRawValue()).subscribe({
      next: (settings) => {
        this.rebuildForm.patchValue(settings);
        this.rebuildForm.markAsPristine();
        this.spinner.hide();
        this.toast.success('Customer report rebuild settings saved');
      },
      error: (error) => {
        console.error('Error saving customer-report rebuild settings:', error);
        this.spinner.hide();
        this.toast.error(`<strong>Failed to save rebuild settings:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }
}
